/**
 * "How much of this was work?" - the dialog that stands between a clock nobody
 * stopped and a line on an invoice.
 *
 * It opens two ways, and the copy at the top has to say which one it is.
 *
 * FORCED: the server refused a plain stop because the clock has been running
 * longer than a person plausibly worked (12 hours; `crm-projects/lib/timer.ts`
 * owns the figure and the reasoning). A timer started on Friday afternoon and
 * found on Monday would otherwise become a sixty-hour line, and the CRM's own
 * rule is that a machine must not decide how much of a forgotten weekend to
 * charge for.
 *
 * CHOSEN: the operator picked "stop and adjust" on a clock of any age, because
 * the run included twenty minutes of lunch and they would rather say so than
 * edit the time entry afterwards.
 *
 * Telling the operator their fresh clock "has been running over 12 hours" would
 * be a plain untruth on screen, so staleness is read here rather than assumed -
 * from the row's own flag OR from the elapsed span, since the list refreshes on
 * an interval and a clock can cross the limit between two refreshes.
 *
 * Either way the field is prefilled with what the clock counted and the operator
 * is expected to be able to change it. The measured span stays on screen beside
 * the field rather than being replaced by it, because the two numbers are
 * different claims - one is what happened, the other is what gets billed - and
 * the audit row records both.
 *
 * HOURS, not minutes, in the field. Every other time form in the CRM speaks in
 * decimal hours with a 0.25 step, and a dialog that asked for "455 minutes"
 * would be the only screen in the product where an operator has to do the
 * division themselves.
 *
 * The third button is not a rescue: throwing the clock away records nothing at
 * all, which is the right answer for a timer left on over a holiday and the
 * wrong one for an afternoon somebody actually worked. It is a danger tone and
 * it sits apart from the other two for that reason.
 */
import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { useT } from "../../i18n/I18nProvider";
import type { RunningTimer } from "../../lib/taskTimer";
import { Btn, Modal, fmtDateTime } from "../ui";
import { toHoursInput } from "../sections/projects/lib";
import { ElapsedClock } from "./ElapsedClock";
import { elapsedSeconds, elapsedToMinutes } from "./elapsed";
import { useTimers, type StartIntent } from "./TimerProvider";

export function TimerStopModal({ timer, startAfter }: { timer: RunningTimer; startAfter?: StartIntent }) {
  const t = useT();
  const timers = useTimers();
  const skewMs = timers?.skewMs ?? 0;

  // Frozen at open. The counter beside the field goes on ticking, which is
  // correct - it is a live reading - but a field that re-filled itself while
  // somebody was typing in it would be unusable.
  const [hours, setHours] = useState(() =>
    toHoursInput(elapsedToMinutes(elapsedSeconds(timer.startedAt, skewMs))),
  );
  const [note, setNote] = useState(timer.note ?? "");
  const [billable, setBillable] = useState(timer.billable);
  const [busy, setBusy] = useState(false);

  if (!timers) return null;

  const minutes = Math.max(1, Math.round(Number(hours) * 60));
  const valid = Number.isFinite(Number(hours)) && Number(hours) > 0;

  // The flag from the list is the server's answer and is trusted when it says
  // yes; the elapsed span is checked as well because the list refreshes on an
  // interval, and a clock that crosses the limit in between would otherwise be
  // described here as fresh. `staleAfterMinutes` is 0 until the first list
  // arrives, which would make every clock stale, so an unloaded limit means no.
  const stale =
    timer.stale === true ||
    (timers.staleAfterMinutes > 0 &&
      elapsedToMinutes(elapsedSeconds(timer.startedAt, skewMs)) >= timers.staleAfterMinutes);

  async function record() {
    if (!timers || !valid) return;
    setBusy(true);
    const out = await timers.stopNow(timer, { minutes, note: note.trim() || undefined, billable });
    // Only when the hours were actually recorded. Starting the next clock after
    // a refusal would leave the operator timing something new while the run they
    // were trying to close is still open.
    if (out.ok && startAfter) await timers.startNow(startAfter.taskId, startAfter.note);
    setBusy(false);
    timers.closePending();
  }

  async function discard() {
    if (!timers) return;
    setBusy(true);
    await timers.discardNow(timer);
    if (startAfter) await timers.startNow(startAfter.taskId, startAfter.note);
    setBusy(false);
    timers.closePending();
  }

  const title = t("timer.stopTitle");

  return (
    <Modal title={title} label={title} onClose={timers.closePending} className="max-w-[460px]">
      <div className="mt-1 flex flex-col gap-3">
        <p className="text-[13.25px] font-semibold">{timer.task?.title ?? t("timer.unknownTask")}</p>

        {/* Why the dialog is here at all, in the words the operator needs. Two
            different reasons, so two different sentences - see the file head. */}
        <p className="crm-mut text-[12.75px] leading-relaxed">
          {stale
            ? t("timer.staleBody", {
                hours: String(Math.round((timers.staleAfterMinutes / 60) * 10) / 10),
              })
            : t("timer.adjustBody")}
        </p>

        <div className="flex items-center gap-3 rounded-2xl bg-[var(--crm-sunk)] px-4 py-3">
          <span className="crm-mut min-w-0 text-[12.5px]">
            {t("timer.startedAt", { at: fmtDateTime(timer.startedAt) })}
          </span>
          <ElapsedClock timer={timer} className="crm-push shrink-0 text-[15px] font-bold" />
        </div>

        <div>
          <span className="crm-lab">{t("timer.recordHours")} *</span>
          <label className="crm-inp">
            <input
              type="number"
              step="0.25"
              min="0"
              inputMode="decimal"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              aria-label={t("timer.recordHours")}
              autoFocus
              required
            />
          </label>
        </div>

        <div>
          <span className="crm-lab">{t("projects.note")}</span>
          <label className="crm-inp">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("projects.notePlaceholder")}
              autoComplete="off"
            />
          </label>
        </div>

        <label className="crm-inp cursor-pointer">
          <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} />
          <span className="text-[13.5px]">{t("projects.billable")}</span>
        </label>

        <div className="crm-m-foot">
          <Btn tone="danger" onClick={() => void discard()} disabled={busy}>
            <Trash2 className="size-4" />
            {t("timer.discard")}
          </Btn>
          <Btn onClick={timers.closePending} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn tone="dark" onClick={() => void record()} disabled={busy || !valid}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("timer.record")}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
